/**
 * services/notificationApi.ts
 * Feature: 253 - Data-Driven Email Notification Preferences (T020)
 * Author: Peter Dobler (Dobler Data Solutions)
 *
 * Frontend client for the notification-preference API. Uses the shared axios
 * apiClient (auth attached by its interceptor). The backend wraps responses in
 * { success, data } — unwrapped here to the DTO.
 */

import { apiClient } from './apiClient';
import type {
  NotificationCatalog,
  NotificationPreferencesResponse,
  UpdatePreferenceRequest,
  NotificationStats,
  NotificationStatsWindow,
  UserPreferenceSummary,
} from '@shared/types/notifications';

export type UserPreferenceSummaryEntry = { userId: string; summary: UserPreferenceSummary };

const CATALOG = '/api/notification-catalog';
const STATS = '/api/notification-stats';
const ME = '/api/notification-preferences/me';
const USERS = '/api/notification-preferences/users';

export async function fetchNotificationStats(window: NotificationStatsWindow = '7d'): Promise<NotificationStats> {
  const res = await apiClient.get<{ success: boolean; data: NotificationStats }>(STATS, { params: { window } });
  return res.data.data;
}

export async function fetchPreferenceSummaries(): Promise<UserPreferenceSummaryEntry[]> {
  const res = await apiClient.get<{ success: boolean; data: UserPreferenceSummaryEntry[] }>(
    '/api/notification-preferences/summary',
  );
  return res.data.data;
}

export async function fetchNotificationCatalog(): Promise<NotificationCatalog> {
  const res = await apiClient.get<{ success: boolean; data: NotificationCatalog }>(CATALOG);
  return res.data.data;
}

export async function fetchMyPreferences(): Promise<NotificationPreferencesResponse> {
  const res = await apiClient.get<{ success: boolean; data: NotificationPreferencesResponse }>(ME);
  return res.data.data;
}

export async function updateMyPreference(body: UpdatePreferenceRequest): Promise<void> {
  await apiClient.put(ME, body);
}

export async function deleteMyPreference(categoryKey: string, targetKey: string): Promise<void> {
  await apiClient.delete(ME, { params: { categoryKey, targetKey } });
}

/** All-on / all-off for a whole category. */
export async function updateMyCategoryBulk(categoryKey: string, optedIn: boolean): Promise<void> {
  await apiClient.put(`${ME}/bulk`, { categoryKey, optedIn });
}

/** Turn off (or on) EVERY notification category in one action. */
export async function updateMyAllNotifications(optedIn: boolean): Promise<void> {
  await apiClient.put(`${ME}/all`, { optedIn });
}

/** DEV only: send a representative test email for a category to yourself. */
export async function sendTestNotificationEmail(categoryKey: string): Promise<{ sent: boolean; to: string }> {
  const res = await apiClient.post<{ success: boolean; data: { sent: boolean; to: string } }>(`${ME}/test`, {
    categoryKey,
  });
  return res.data.data;
}

// ── Admin manage-on-behalf (used by the Users screen, T040) ──────────────────

export async function fetchUserPreferences(userId: string): Promise<NotificationPreferencesResponse> {
  const res = await apiClient.get<{ success: boolean; data: NotificationPreferencesResponse }>(`${USERS}/${userId}`);
  return res.data.data;
}

export async function updateUserPreference(userId: string, body: UpdatePreferenceRequest): Promise<void> {
  await apiClient.put(`${USERS}/${userId}`, body);
}
